import { Anchor, Badge, Button, CopyButton, Group, Stack, Text } from "@mantine/core";
import { getMedicalHistoryView } from "../../utils/travellerDrawerConstants";
import classes from "./TravellerPolicyDrawer.module.css";

const Heading = ({ children }) => (
  <Text size="xs" fw={700} tt="uppercase" c="orange.7" mb={4}>{children}</Text>
);

const Yn = ({ yes }) => (
  <Badge size="sm" variant="light" color={yes ? "orange" : "teal"} radius="xl">
    {yes ? "Yes" : "No"}
  </Badge>
);

const Row = ({ label, children }) => (
  <div className={classes.mhRow}>
    <Text size="sm" c="dimmed" className={classes.mhLabel}>{label}</Text>
    <div className={classes.mhValue}>{children}</div>
  </div>
);

const Rows = ({ items }) =>
  items.map((item) => (
    <Row key={item.label} label={item.label}>
      {item.vaccines ? (
        <Group gap={6} justify="flex-end" wrap="wrap">
          {item.vaccines.map((shot) => (
            <Badge
              key={shot.label}
              variant="outline"
              color={shot.on ? "blue" : "gray"}
              radius="xl"
              tt="none"
            >
              {shot.on ? "✓" : "☐"} {shot.label}
            </Badge>
          ))}
        </Group>
      ) : item.yes === undefined ? (
        <Text size="sm" fw={item.value === "None" ? 400 : 600} c={item.value === "None" ? "dimmed" : undefined}>
          {item.value}
        </Text>
      ) : (
        <Group gap={6} justify="flex-end" wrap="wrap">
          <Yn yes={item.yes} />
          {item.yes && item.detail ? (
            <Text size="xs" c="dimmed" className={classes.mhDetail}>• {item.detail}</Text>
          ) : null}
        </Group>
      )}
    </Row>
  ));

const TravellerDrawerMedicalHistory = ({ traveller }) => {
  const view = getMedicalHistoryView(traveller);

  if (!view.hasData && !view.url) {
    return <Text size="sm" c="dimmed">No medical history on file.</Text>;
  }

  return (
    <Stack gap="lg">
      {view.general.length ? (
        <div>
          <Heading>General Health</Heading>
          <Rows items={view.general} />
        </div>
      ) : null}

      {view.declared.length || view.conditions.length ? (
        <div>
          <Heading>Medical History</Heading>
          {view.declared.length ? (
            <div className={classes.mhDeclared}>
              <Text size="xs" fw={700} tt="uppercase" c="orange.7" mb="sm">
                Declared during booking
              </Text>
              <Group gap={8} wrap="wrap">
                {view.declared.map((label) => (
                  <Badge key={label} variant="outline" color="orange" radius="xl" tt="none" className={classes.mhChip}>
                    ✓ {label}
                  </Badge>
                ))}
              </Group>
            </div>
          ) : null}
          <Rows items={view.conditions} />
        </div>
      ) : null}

      {view.current.length ? (
        <div>
          <Heading>Current Condition</Heading>
          <Rows items={view.current} />
        </div>
      ) : null}

      {view.fitness.length ? (
        <div>
          <Heading>Fitness</Heading>
          <Rows items={view.fitness} />
        </div>
      ) : null}

      {view.declaration.length ? (
        <div>
          <Heading>Declaration</Heading>
          <Rows items={view.declaration} />
        </div>
      ) : null}

      {view.url ? (
        <Stack gap={6}>
          <Heading>Medical History URL</Heading>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Anchor
              href={view.url}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              className={classes.medicalUrl}
            >
              {view.url}
            </Anchor>
            <CopyButton value={view.url} timeout={2000}>
              {({ copied, copy }) => (
                <Button size="compact-xs" variant="light" color={copied ? "teal" : "brand"} onClick={copy}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default TravellerDrawerMedicalHistory;
